import { Button, MenuItem, Select } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import useToastContext from "hooks/useToastContext";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TiTimes, TiTrash } from "react-icons/ti";
import { apiGet } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import Input from "../Input";

const sx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "900px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};
export default function QuestionDialog({
  testId: initialTestId = "",
  questionBankId: initialQuestionBankId = "",
  type: modalType = "add",
  open = false,
  question: q,
  questionAddedHandler,
  questionUpdatedHandler,
  closeHandler,
}) {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [tests, setTests] = useState([]);
  const [questionBanks, setQuestionBanks] = useState([]);
  const [title, setTitle] = useState(q?.title || "");
  const [titleAr, setTitleAr] = useState(q?.titleAr || "");
  const [expectedAnswer, setExpectedAnswer] = useState(q?.expectedAnswer || "");
  const [points, setPoints] = useState(q?.points + "" || "");
  const [type, setType] = useState(q?.type || "single");
  const [options, setOptions] = useState(q?.options || []);
  const [matchingPairs, setMatchingPairs] = useState(q?.matchingPairs || []);
  const [isTrue, setIsTrue] = useState(q?.isTrue || false);
  const [testId, setTestId] = useState(q?.testId?._id || "");
  const [questionBankId, setQuestionBankId] = useState(
    q?.questionBankId?._id || ""
  );
  useEffect(() => {
    if (!q || isEmpty(q)) return;
    setTitle(q?.title || "");
    setTitleAr(q?.titleAr || "");
    setExpectedAnswer(q?.expectedAnswer || "");
    setPoints(q?.points + "" || "");
    setType(q?.type || "single");
    setOptions(q?.options || []);
    setMatchingPairs(q?.matchingPairs || []);
    setIsTrue(q?.isTrue || false);
    setTestId(q?.testId?._id || "");
    setQuestionBankId(q?.questionBankId?._id || "");
  }, [q]);

  function closeModalHandler() {
    setTitle("");
    setTitleAr("");
    setExpectedAnswer("");
    setMatchingPairs([]);
    setPoints("");
    setType("single");
    setOptions([]);
    setIsTrue(false);
    setTestId("");
    setQuestionBankId("");
    closeHandler && closeHandler();
  }
  function addQuestionHandler() {
    questionAddedHandler &&
      questionAddedHandler({
        title,
        titleAr,
        type,
        options,
        expectedAnswer,
        points,
        isTrue,
        matchingPairs,
        testId,
        questionBankId,
      });
    closeModalHandler();
  }

  function questionEditHandler() {
    questionUpdatedHandler &&
      questionUpdatedHandler({
        ...q,
        title,
        titleAr,
        type,
        options,
        expectedAnswer,
        points,
        isTrue,
        matchingPairs,
        testId,
        questionBankId,
      });
    closeModalHandler();
  }
  function isValid() {
    const isRequiredFieldsValid =
      isEmpty(title?.trim()) ||
      isEmpty(titleAr?.trim()) ||
      isEmpty(points?.trim());

    if (type === "single" || type === "multiple") {
      if (options.length === 0) return false;

      const hasEmptyOption = options.some(
        (option) => option.textAr === "" || option.text === ""
      );
      if (hasEmptyOption) return false;
      const hasCorrectOption = options.find((option) => !!option.isCorrect);
      if (!hasCorrectOption) return false;
    }
    if (type === "matching" && matchingPairs.length === 0) return false;
    if (
      type === "matching" &&
      matchingPairs.some(
        (pair) =>
          pair.left.text === "" ||
          pair.left.textAr === "" ||
          pair.right.text === "" ||
          pair.right.textAr === ""
      )
    )
      return false;
    if (type === "essay" && expectedAnswer === "") return false;
    if (isRequiredFieldsValid) return false;
    if (!points || points <= 0 || isNaN(points) || isEmpty(points))
      return false;

    return true;
  }

  function addOptionHandler() {
    setOptions([...options, { text: "", textAr: "", isCorrect: false }]);
  }

  function canAddNewOption() {
    const hasEmptyOption = options.some(
      (option) => option.text === "" || option.textAr === ""
    );
    if (hasEmptyOption) return false;
    if (options.length >= 5) return false;

    return true;
  }
  function canAddNewPair() {
    const hasEmptyPair = matchingPairs.some(
      (pair) =>
        pair.left.text === "" ||
        pair.left.textAr === "" ||
        pair.right.text === "" ||
        pair.right.textAr === ""
    );
    if (matchingPairs.length >= 5) return false;
    if (hasEmptyPair) return false;

    return true;
  }

  // For edit
  useEffect(() => {
    async function getTests() {
      try {
        const result = await apiGet(apiPath.quizTests);
        setTests(result?.data?.results?.docs || []);
      } catch (error) {
        console.error(error);
        notification.error(error?.response?.data?.message);
      }
    }
    async function getQuestionBanks() {
      try {
        const result = await apiGet(apiPath.quizQuestionBank);
        setQuestionBanks(result?.data?.results?.docs || []);
        console.log(result?.data?.results);
      } catch (error) {
        console.error(error);
        notification.error(error?.response?.data?.message);
      }
    }
    if (modalType === "edit" || modalType === "create") {
      getTests();
      getQuestionBanks();
    }
  }, [modalType, notification]);

  return (
    <Modal
      open={open}
      onClose={closeModalHandler}
      aria-labelledby="modal-title"
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Box sx={sx}>
        <div className="flex flex-col h-full max-h-[90vh]">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2
              id="modal-title"
              className="text-2xl font-semibold text-gray-800"
            >
              {modalType === "edit"
                ? t("O_EDIT_QUESTION")
                : t("O_ADD_QUESTION")}
            </h2>
            <button
              onClick={closeModalHandler}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all duration-200 flex items-center justify-center"
              type="button"
              aria-label="Close"
            >
              <TiTimes size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="flex flex-col gap-4">
              {modalType === "edit" || modalType === "create" ? (
                <>
                  <Select
                    onChange={(e) => setTestId(e.target.value)}
                    value={initialTestId || testId}
                    label={t("O_TEST")}
                    placeholder={t("O_TEST")}
                    disabled={!!initialTestId}
                  >
                    <MenuItem value="">{t("O_NONE")}</MenuItem>
                    {tests?.map((test) => (
                      <MenuItem key={test._id} value={test._id}>
                        {test.title}
                      </MenuItem>
                    ))}
                  </Select>

                  <Select
                    onChange={(e) => setQuestionBankId(e.target.value)}
                    value={initialQuestionBankId || questionBankId}
                    label={t("O_QUESTION_BANK")}
                    placeholder={t("O_QUESTION_BANK")}
                    disabled={!!initialQuestionBankId}
                  >
                    <MenuItem value="">{t("O_NONE")}</MenuItem>
                    {questionBanks.map((questionBank) => (
                      <MenuItem key={questionBank._id} value={questionBank._id}>
                        {questionBank.title}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              ) : null}
              {/* {modalType === "edit" ? <Select /> : null} */}
              <div className="flex gap-4">
                <Input
                  required
                  label={t("O_TITLE")}
                  placeholder={t("O_TITLE")}
                  type="text"
                  onChange={(e) => setTitle(e.target.value)}
                  value={title}
                />
                <Input
                  required
                  label={t("O_TITLE") + t("O_ARABIC")}
                  placeholder={t("O_TITLE") + t("O_ARABIC")}
                  type="text"
                  name="title"
                  onChange={(e) => setTitleAr(e.target.value)}
                  value={titleAr}
                />
              </div>

              <select
                name="type"
                id="type"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                onChange={(e) => {
                  setOptions([]);
                  setMatchingPairs([]);
                  setExpectedAnswer("");
                  setIsTrue(false);
                  setType(e.target.value);
                }}
                value={type}
              >
                <option value="single">
                  {t("O_MULTIPLE_CHOICE_SINGLE_ANSWER")}
                </option>
                <option value="multiple">
                  {t("O_MULTIPLE_CHOICE_MULTIPLE_ANSWER")}
                </option>
                <option value="essay">{t("O_ESSAY")}</option>
                <option value="truefalse">{t("O_TRUE_OR_FALSE")}</option>
                <option value="matching">{t("O_MATCHING")}</option>
              </select>

              {type === "truefalse" && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isTrue"
                    id="truefalse"
                    onChange={(e) => setIsTrue(e.target.checked)}
                    value={isTrue}
                    checked={isTrue}
                  />
                  <label htmlFor="truefalse">{t("O_IS_TRUE")}</label>
                </div>
              )}
              {type === "single" && (
                <div className="flex flex-col gap-4">
                  {options
                    ? options.map((option, index) => (
                        <OptionCard
                          key={index}
                          option={option}
                          index={index}
                          setOptions={setOptions}
                          options={options}
                          singleChoice={true}
                        />
                      ))
                    : null}

                  <Button
                    type="button"
                    variant="contained"
                    disabled={!canAddNewOption()}
                    className={`bg-blue-500 text-white px-4 py-2 rounded-md w-min text-nowrap self-end ${
                      !canAddNewOption() ? "bg-gray-500" : ""
                    }`}
                    onClick={addOptionHandler}
                  >
                    {t("O_ADD_OPTION")}
                  </Button>
                </div>
              )}
              {type === "multiple" && (
                <div className="flex flex-col gap-4">
                  {options
                    ? options.map((option, index) => (
                        <OptionCard
                          option={option}
                          index={index}
                          setOptions={setOptions}
                          options={options}
                        />
                      ))
                    : null}
                  <Button
                    type="button"
                    variant="contained"
                    disabled={!canAddNewOption()}
                    className={`bg-blue-500 text-white px-4 py-2 rounded-md w-min text-nowrap self-end ${
                      !canAddNewOption() ? "bg-gray-500" : ""
                    }`}
                    onClick={addOptionHandler}
                  >
                    {t("O_ADD_OPTION")}
                  </Button>
                </div>
              )}
              {type === "essay" && (
                <div className="flex flex-col gap-4">
                  <textarea
                    name="essay"
                    id="essay"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder={t("O_EXPECTED_ANSWER")}
                    onChange={(e) => setExpectedAnswer(e.target.value)}
                    value={expectedAnswer}
                  />
                </div>
              )}
              {type === "matching" && (
                <div className="flex flex-col gap-4">
                  {matchingPairs
                    ? matchingPairs.map((pair, index) => (
                        <>
                          <MatchingCard
                            key={index}
                            pair={pair}
                            index={index}
                            setMatchingPairs={setMatchingPairs}
                            matchingPairs={matchingPairs}
                          />
                          <hr />
                        </>
                      ))
                    : null}

                  <button
                    type="button"
                    disabled={!canAddNewPair()}
                    className={`bg-blue-500 text-white px-4 py-2 rounded-md w-min text-nowrap self-end ${
                      !canAddNewPair() ? "bg-gray-500" : ""
                    }`}
                    onClick={() =>
                      setMatchingPairs([
                        ...matchingPairs,
                        {
                          left: {
                            textAr: "",
                            text: "",
                          },
                          right: {
                            textAr: "",
                            text: "",
                          },
                        },
                      ])
                    }
                  >
                    {t("O_ADD_OPTION")}
                  </button>
                </div>
              )}
              <input
                type="number"
                name="points"
                id="points"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                placeholder={t("O_POINTS")}
                onChange={(e) => setPoints(e.target.value)}
                value={points}
              />
            </div>
          </div>

          <div className="flex-shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
            <button
              type="button"
              className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium"
              onClick={closeModalHandler}
            >
              {t("O_CANCEL") || "Cancel"}
            </button>
            <button
              type="button"
              className={`px-6 py-2.5 rounded-lg transition-colors duration-200 font-medium shadow-sm ${
                !isValid()
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              disabled={!isValid()}
              onClick={
                modalType === "edit" ? questionEditHandler : addQuestionHandler
              }
            >
              {modalType === "edit"
                ? t("O_UPDATE_QUESTION")
                : t("O_ADD_QUESTION")}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

function OptionCard({
  option,
  index,
  setOptions,
  options,
  singleChoice = false,
}) {
  const { t } = useTranslation();

  function titleChangeHandler(e) {
    setOptions(
      options.map((o, i) => (i === index ? { ...o, text: e.target.value } : o))
    );
  }
  function titleArChangeHandler(e) {
    setOptions(
      options.map((o, i) =>
        i === index ? { ...o, textAr: e.target.value } : o
      )
    );
  }

  function isCorrectChangeHandler(e) {
    setOptions(
      options.map((o, i) =>
        i === index ? { ...o, isCorrect: e.target.checked } : o
      )
    );
  }
  function deleteOptionHandler() {
    setOptions((i) => i.filter((_, i) => i !== index));
  }
  return (
    <div className="flex flex-col gap-0">
      {index > 0 && <hr className="border-gray-200 mb-2" />}
      <h3 className="text-md font-bold text-gray-700">
        {t("O_OPTION")} {index + 1}
      </h3>
      <div className="flex flex-row gap-4 items-center ">
        <button
          type="button"
          className="bg-red-500 text-white p-2 rounded-full"
          onClick={deleteOptionHandler}
        >
          <TiTrash size={20} />
        </button>
        <Input
          required
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          type="text"
          hideLabel
          name="title"
          label={t("O_TITLE")}
          placeholder={t("O_TITLE")}
          value={option.text}
          onChange={titleChangeHandler}
        />
        <Input
          required
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          type="text"
          hideLabel
          name="titleAr"
          label={t("O_TITLE") + t("O_ARABIC")}
          placeholder={t("O_TITLE") + t("O_ARABIC")}
          value={option.textAr}
          onChange={titleArChangeHandler}
        />
        <div className="flex items-center gap-2">
          <input
            type={singleChoice ? "radio" : "checkbox"}
            name="isCorrect"
            id={`isCorrect${index}`}
            checked={option.isCorrect}
            onChange={isCorrectChangeHandler}
          />
          <label
            htmlFor={`isCorrect${index}`}
            className="text-sm font-medium text-gray-700 text-nowrap"
          >
            {t("O_CORRECT")}
          </label>
        </div>
      </div>
    </div>
  );
}

function MatchingCard({ pair, index, setMatchingPairs, matchingPairs }) {
  const { t } = useTranslation();

  function deletePairHandler() {
    setMatchingPairs((i) => i.filter((_, i) => i !== index));
  }
  return (
    <div className="flex flex-col gap-0">
      <h3 className="text-md font-bold text-gray-700">
        {t("O_PAIR")} {index + 1}
      </h3>
      <div className="flex flex-row gap-4 items-center ">
        <button
          type="button"
          className="bg-red-500 text-white p-2 rounded-full"
          onClick={deletePairHandler}
        >
          <TiTrash size={20} />
        </button>
        <div className="flex flex-col gap-1 flex-1">
          <Input
            required
            className=" flex-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            type="text"
            hideLabel
            name="title"
            label={t("O_TITLE_LEFT")}
            placeholder={t("O_TITLE_LEFT")}
            value={pair.left.text}
            onChange={(e) =>
              setMatchingPairs(
                matchingPairs.map((o, i) =>
                  i === index
                    ? { ...o, left: { ...o.left, text: e.target.value } }
                    : o
                )
              )
            }
          />
          <Input
            required
            className=" flex-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            type="text"
            hideLabel
            name="titleAr"
            label={t("O_TITLE_LEFT") + t("O_ARABIC")}
            placeholder={t("O_TITLE_LEFT") + t("O_ARABIC")}
            value={pair.left.textAr}
            onChange={(e) =>
              setMatchingPairs(
                matchingPairs.map((o, i) =>
                  i === index
                    ? { ...o, left: { ...o.left, textAr: e.target.value } }
                    : o
                )
              )
            }
          />
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <Input
            required
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            type="text"
            hideLabel
            name="title"
            label={t("O_TITLE_RIGHT")}
            placeholder={t("O_TITLE_RIGHT")}
            value={pair.right.text}
            onChange={(e) =>
              setMatchingPairs(
                matchingPairs.map((o, i) =>
                  i === index
                    ? { ...o, right: { ...o.right, text: e.target.value } }
                    : o
                )
              )
            }
          />
          <Input
            required
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            type="text"
            hideLabel
            name="titleAr"
            label={t("O_TITLE_RIGHT") + t("O_ARABIC")}
            placeholder={t("O_TITLE_RIGHT") + t("O_ARABIC")}
            value={pair.right.textAr}
            onChange={(e) =>
              setMatchingPairs(
                matchingPairs.map((o, i) =>
                  i === index
                    ? { ...o, right: { ...o.right, textAr: e.target.value } }
                    : o
                )
              )
            }
          />
        </div>
      </div>
    </div>
  );
}
